/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ChevronRight, ExternalLink, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import { CORE_MODULE, type LegacyModule, type ModuleBranchId, type ModuleRegistryConnection, type ModuleSummary } from '../../api/areas/modules';
import { DataTable, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EmptyState, Loading } from '../../components/States';
import { useRetained } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { OnOffBadge, Panel, StatusBadge, tx } from '../integrations/shared';
import { ModuleCard } from './ModuleCard';
import { InstanceStatusBadge, modulesUrl, useText, VersionValues } from './ui';

/** The providers of ADR-016 not wrapped as modules yet, each with its own settings page. */
function LegacyPanel({ items }: { items: readonly LegacyModule[] }) {
  const { t } = useTranslation('modules');
  const text = useText();
  return (
    <Panel title={t('branchTab.legacyTitle')} description={t('branchTab.legacyHint')}>
      <ul className="divide-y">
        {items.map((l) => (
          <li key={l.provider} className="flex flex-wrap items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="min-w-0 space-y-1">
              <p className="font-medium">{text(l.name)}</p>
              <p className="text-sm text-muted-foreground">{text(l.summary)}</p>
              <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{l.vendor.name}</span>
                {l.configured_in_database && l.version !== null ? (
                  <>
                    <span>{t('branchTab.legacyConfigured', { version: l.version })}</span>
                    {l.enabled !== null ? <OnOffBadge on={l.enabled} /> : null}
                  </>
                ) : (
                  <span>{t('branchTab.legacyNotConfigured')}</span>
                )}
              </p>
            </div>
            <Button size="sm" variant="outline" asChild>
              <Link to={l.page}>
                <ExternalLink /> {t('branchTab.openPage')}
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** The registry branch's connections (their own pages, ADR-016), read-through. */
function ConnectionsPanel({ items }: { items: readonly ModuleRegistryConnection[] }) {
  const { t } = useTranslation('modules');
  const columns: Column<ModuleRegistryConnection>[] = [
    {
      key: 'name',
      header: t('branchTab.connectionColumns.name'),
      cell: (c) => (
        <span className="grid gap-0.5">
          <Link to={c.page} className="font-medium text-primary hover:underline">
            {c.name}
          </Link>
          <code className="text-xs text-muted-foreground">{c.code}</code>
        </span>
      ),
    },
    { key: 'kind', header: t('branchTab.connectionColumns.kind'), cell: (c) => tx(`kind.${c.connection_kind}`, c.connection_kind) },
    { key: 'protocol', header: t('branchTab.connectionColumns.protocol'), cell: (c) => tx(`protocol.${c.protocol}`, c.protocol) },
    { key: 'module', header: t('branchTab.connectionColumns.module'), cell: (c) => tx(`adapter.${c.module}`, c.module) },
    { key: 'status', header: t('branchTab.connectionColumns.status'), cell: (c) => <StatusBadge group="connectionStatus" value={c.status} /> },
  ];
  return (
    <Panel title={t('branchTab.connectionsTitle')} description={t('branchTab.connectionsHint')} flush>
      <DataTable columns={columns} rows={items} rowKey={(c) => c.id} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('branchTab.connectionsNone')}</p>} />
    </Panel>
  );
}

/**
 * The branch core's own settings (instance `core` / `default`): the values in force, its status,
 * and its page, where they are edited, applied and approved like any instance's.
 */
function CoreSettingsCard({ branch, core }: { branch: ModuleBranchId; core: ModuleSummary }) {
  const { t } = useTranslation('modules');
  const instance = core.instances[0];
  const fields = core.manifest.settings.fields;
  return (
    <Panel
      className="gap-3 py-4"
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('branchTab.coreSettings')}
          {instance ? <InstanceStatusBadge status={instance.status} /> : null}
        </span>
      }
      description={t('branchTab.coreSettingsHint')}
      actions={
        <Button size="sm" variant="outline" asChild>
          <Link to={modulesUrl(branch, CORE_MODULE, instance?.key ?? 'default', 'config')}>
            <Pencil /> {t('branchTab.coreEdit')}
          </Link>
        </Button>
      }
    >
      {instance?.active ? (
        // Closed by default so the branch's modules stay in view; the values are one click away.
        <details className="group">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
            <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden="true" />
            {t('branchTab.coreValues')}
          </summary>
          <div className="mt-3">
            <VersionValues version={instance.active} fields={fields} />
          </div>
        </details>
      ) : (
        <p className="text-sm text-muted-foreground">{t('branchTab.coreDefaults')}</p>
      )}
    </Panel>
  );
}

/**
 * One business branch (tab of Cài đặt › Module): its core's settings, modules, legacy providers and
 * connections. A branch without any module (the product branches until a vendor is chosen) says so,
 * even when its core's settings are shown.
 */
export function BranchTab({ branch }: { branch: ModuleBranchId }) {
  const { t } = useTranslation('modules');
  const [tick, setTick] = useState(0);
  const result = useApi((signal) => api.getModuleBranch(branch, { signal }), [branch, tick]);
  const data = useRetained(result.data);
  const reload = () => setTick((n) => n + 1);
  if (result.status === 'error' && !data) return <DataError error={result.error} onRetry={result.reload} title={t('page.loadFailed')} />;
  if (!data || data.branch.id !== branch) return <Loading rows={4} />;
  const b = data.branch;
  const noModule = !b.modules.length && !b.legacy.length && !b.connections?.length;
  // The branch's own modules only (not its core, nor the providers configured on their own page). The
  // registry's connections are `registry.registries` rows, not module instances: its own table counts them.
  const instances = b.modules.flatMap((m) => m.instances);
  // Registry: each connection shows under the module it uses (owner 2026-10-07: a separate table
  // above cards reading "0 kết nối" was wrong); a connection of no listed module keeps the table.
  const moduleIds = new Set(b.modules.map((m) => m.manifest.id));
  const connectionsOf = (id: string) => (b.connections ?? []).filter((c) => c.module === id);
  const orphans = (b.connections ?? []).filter((c) => !moduleIds.has(c.module));
  const total = instances.length + (b.connections?.length ?? 0);
  const on = instances.filter((i) => i.enabled).length + (b.connections ?? []).filter((c) => c.status === 'ACTIVE').length;
  const summary = [t('branchTab.summary', { count: b.modules.length }), total ? t('branchTab.summaryOn', { count: total, on }) : null];
  return (
    <div className="space-y-4" data-module-branch={branch}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm text-muted-foreground">{t(`branchHint.${branch}`)}</p>
        {b.modules.length ? <p className="text-xs tabular text-muted-foreground">{summary.filter(Boolean).join(' · ')}</p> : null}
      </div>
      {b.core ? <CoreSettingsCard branch={branch} core={b.core} /> : null}
      {orphans.length ? <ConnectionsPanel items={orphans} /> : null}
      {b.modules.map((m) => (
        <ModuleCard
          key={m.manifest.id}
          summary={m}
          env={data.environment}
          canManage={data.can_manage}
          onChanged={reload}
          connections={b.connections ? connectionsOf(m.manifest.id) : undefined}
          addConnectionTo={b.connections ? '/settings?tab=connections&sub=registries' : undefined}
        />
      ))}
      {b.legacy.length ? <LegacyPanel items={b.legacy} /> : null}
      {noModule ? <EmptyState title={t('branchTab.noModules')}>{t('branchTab.noModulesHint')}</EmptyState> : null}
    </div>
  );
}
